class ProdaProductShowcase extends HTMLElement {
  #track;
  #slides = [];
  #activeSlide;
  #previousButton;
  #nextButton;
  #scrollFrame;
  #settleTimer;
  #pendingSlide;
  #resizeObserver;
  #notesObserver;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #twoUp = window.matchMedia('(max-width: 749px)');
  #fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    this.#track = this.querySelector('[data-showcase-track]');
    this.#slides = [...this.querySelectorAll('[data-showcase-slide]')];
    this.#activeSlide = this.querySelector('[data-initial]') || this.#slides[0];
    this.#previousButton = this.querySelector('[data-showcase-previous]');
    this.#nextButton = this.querySelector('[data-showcase-next]');

    if (!this.#track || !this.#activeSlide) return;

    this.addEventListener('click', this.#handleClick);
    this.#track.addEventListener('pointerdown', this.#handleManualScroll, { passive: true });
    this.#track.addEventListener('wheel', this.#handleManualScroll, { passive: true });
    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#track.addEventListener('scrollend', this.#handleSettle, { passive: true });
    this.addEventListener('pointerover', this.#handleMediaIntent, { passive: true });
    this.addEventListener('focusin', this.#handleMediaIntent);

    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.hasAttribute('data-ready')) return;
      this.#align(this.#activeSlide, 'auto');
    });
    this.#resizeObserver.observe(this.#track);

    this.#align(this.#activeSlide, 'auto');
    this.#setActive(this.#activeSlide, false);
    this.#observeNotesMedia();
    this.#setControlVisibility();
    this.setAttribute('data-ready', 'true');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.#track?.removeEventListener('scrollend', this.#handleSettle);
    this.#track?.removeEventListener('pointerdown', this.#handleManualScroll);
    this.#track?.removeEventListener('wheel', this.#handleManualScroll);
    this.removeEventListener('pointerover', this.#handleMediaIntent);
    this.removeEventListener('focusin', this.#handleMediaIntent);
    this.#resizeObserver?.disconnect();
    this.#notesObserver?.disconnect();
    this.#pendingSlide = null;
    cancelAnimationFrame(this.#scrollFrame);
    window.clearTimeout(this.#settleTimer);
  }

  #scrollPaddingStart() {
    if (!this.#track) return 0;
    return Number.parseFloat(getComputedStyle(this.#track).scrollPaddingInlineStart) || 0;
  }

  #align(slide, behavior = 'smooth') {
    if (!slide || !this.#track) return;
    const left = this.#twoUp.matches
      ? slide.offsetLeft - this.#scrollPaddingStart()
      : slide.offsetLeft - (this.#track.clientWidth - slide.offsetWidth) / 2;
    this.#track.scrollTo({
      left,
      behavior: this.#reducedMotion.matches ? 'auto' : behavior,
    });
  }

  #move(direction) {
    const activeIndex = Math.max(0, this.#slides.indexOf(this.#activeSlide));
    const destination = this.#slides[activeIndex + direction];
    if (!destination) {
      this.#updateControls();
      return;
    }
    this.#pendingSlide = destination;
    this.#setActive(destination);
    this.#align(destination);
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('[data-showcase-previous], [data-showcase-next]')
      : null;

    if (!target || !this.contains(target) || target.matches(':disabled')) return;
    this.#move(target.hasAttribute('data-showcase-next') ? 1 : -1);
  };

  #handleManualScroll = () => {
    this.#pendingSlide = null;
  };

  #handleMediaIntent = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-showcase-slide]') : null;
    if (!target || !this.contains(target)) return;
    this.#hydrateNotesImage(target);
  };

  #hydrateNotesImage(slide) {
    const image = slide?.querySelector('[data-showcase-notes-image][data-src]');
    if (!(image instanceof HTMLImageElement)) return;

    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-sizes');
  }

  #hydrateNearbyNotes(slide) {
    if (!this.#fineHover.matches) return;
    const activeIndex = this.#slides.indexOf(slide);
    if (activeIndex < 0) return;

    for (const item of this.#slides.slice(Math.max(0, activeIndex - 1), activeIndex + 2)) {
      this.#hydrateNotesImage(item);
    }
  }

  #observeNotesMedia() {
    if (!this.#fineHover.matches) return;

    this.#hydrateNearbyNotes(this.#activeSlide);
    if (!('IntersectionObserver' in window)) return;

    this.#notesObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#hydrateNotesImage(entry.target);
          this.#notesObserver?.unobserve(entry.target);
        }
      },
      {
        root: this.#track,
        rootMargin: '0px 50% 0px 50%',
        threshold: 0.01,
      }
    );

    for (const slide of this.#slides) this.#notesObserver.observe(slide);
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      if (this.#pendingSlide) {
        this.#setActive(this.#pendingSlide, false);
        return;
      }

      const trackAnchor = this.#twoUp.matches
        ? this.#track.scrollLeft + this.#scrollPaddingStart()
        : this.#track.scrollLeft + this.#track.clientWidth / 2;
      const nearest = this.#slides.reduce((closest, slide) => {
        const slideAnchor = this.#twoUp.matches ? slide.offsetLeft : slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideAnchor - trackAnchor);
        return distance < closest.distance ? { slide, distance } : closest;
      }, { slide: this.#activeSlide, distance: Number.POSITIVE_INFINITY }).slide;

      if (nearest && nearest !== this.#activeSlide) this.#setActive(nearest, false);
    });

    window.clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(this.#handleSettle, 240);
  };

  #handleSettle = () => {
    if (this.#pendingSlide) {
      const destination = this.#pendingSlide;
      this.#pendingSlide = null;
      this.#setActive(destination, false);
      this.#align(destination, 'auto');
    }
  };

  #setActive(slide, announce = true) {
    this.#activeSlide = slide;
    this.#hydrateNearbyNotes(slide);

    for (const item of this.#slides) {
      const isActive = item === slide;
      if (isActive) {
        item.setAttribute('data-active', 'true');
      } else {
        item.removeAttribute('data-active');
      }
      if (isActive) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    }

    this.#updateControls();

    if (!announce) return;
    const status = this.querySelector('[data-showcase-status]');
    if (status) status.textContent = `${slide.dataset.productTitle} selected`;
  }

  #setControlVisibility() {
    const shouldHide = this.#slides.length < 2;
    if (this.#previousButton) this.#previousButton.hidden = shouldHide;
    if (this.#nextButton) this.#nextButton.hidden = shouldHide;
    this.#updateControls();
  }

  #updateControls() {
    const activeIndex = this.#slides.indexOf(this.#activeSlide);
    if (this.#previousButton) this.#previousButton.disabled = activeIndex <= 0;
    if (this.#nextButton) this.#nextButton.disabled = activeIndex < 0 || activeIndex >= this.#slides.length - 1;
  }
}

if (!customElements.get('proda-product-showcase')) {
  customElements.define('proda-product-showcase', ProdaProductShowcase);
}
